import { CobaltBreadcrumb } from '@cae/cobalt-react/components/breadcrumb';
import { CobaltPageHeader } from '@cae/cobalt-react/components/page-header';
import { CobaltSearchField } from '@cae/cobalt-react/components/search-field';
import { debounce } from '@cae/cobalt-react/utils';
import { SEARCH_DEBOUNCE_MS } from '@shared/utils/search/search.utils';
import * as React from 'react';

import type { DocumentsSearchViewProps } from './DocumentsSearchView.types';

import { DocumentsSearchViewContent } from './documents-search-view-content/DocumentsSearchViewContent';
import styles from './DocumentsSearchView.module.css';

const DocumentsSearchView = React.memo(
  ({
    title,
    documentType,
    organizationId,
    canDownload,
    onBack,
  }: Readonly<DocumentsSearchViewProps>): React.ReactElement => {
    const [localValue, setLocalValue] = React.useState('');
    const [searchText, setSearchText] = React.useState('');

    const debouncedSetSearch = React.useMemo(
      () =>
        debounce((value: string) => setSearchText(value), SEARCH_DEBOUNCE_MS),
      []
    );

    React.useEffect(() => {
      return () => debouncedSetSearch.cancel();
    }, [debouncedSetSearch]);

    const handleSearch = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        setLocalValue(value);

        const effectiveLength = value.replaceAll(/\s/g, '').length;
        if (effectiveLength >= 1) {
          debouncedSetSearch(value);
        } else {
          debouncedSetSearch.cancel();
          setSearchText('');
        }
      },
      [debouncedSetSearch]
    );

    const handleClear = React.useCallback((): void => {
      setLocalValue('');
      debouncedSetSearch.cancel();
      setSearchText('');
    }, [debouncedSetSearch]);

    return (
      <div
        className={styles.documentsSearchView}
        data-testid="documents-search-view"
      >
        <CobaltPageHeader
          title={title}
          breadcrumbs={
            <CobaltBreadcrumb variant="back-navigation" onBackClick={onBack}>
              Back
            </CobaltBreadcrumb>
          }
          content={
            <CobaltSearchField
              hasClear
              placeholder="Search by document name"
              value={localValue}
              onChange={handleSearch}
              onClear={handleClear}
              className={styles.searchField}
            />
          }
        />
        <div
          className={styles.searchResultContent}
          data-testid="search-results"
        >
          <DocumentsSearchViewContent
            searchText={searchText}
            documentType={documentType}
            organizationId={organizationId}
            canDownload={canDownload}
          />
        </div>
      </div>
    );
  }
);

DocumentsSearchView.displayName = 'DocumentsSearchView';

export { DocumentsSearchView };
